import React, { FC, useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { useStopwatch } from 'react-timer-hook';
import { IdleTimer } from 'common/components';
import { DEFAULT_VALUE_0, EVENT_IDLE_LIMIT_MS } from 'common/config';
import { useAppDispatch, useAppSelector } from 'common/hooks';
import { EditOutlined, PauseCircleOutlined, PlayCircleOutlined } from 'common/const/svg';
import { history } from 'app/store';
import { InactivityModal, TimeEditorModal } from 'entities/Modal/components';
import { getStopwatchOffset, getTimerFormattedValue, getTimerInfo, timerValueToMilliseconds } from 'entities/Timer/Timer.helper';
import { EEventActionType } from 'entities/Events/Event.enum';
import { EModalKey } from 'entities/Modal/Modal.enum';
import { IEvent } from 'entities/Events/Event.models';

interface IComponentProps {
  event: IEvent | null;
  canStart: boolean;
  autoStart: boolean;
}

export const Timer: FC<IComponentProps> = ({ event, canStart, autoStart }) => {
  const { timer, timerLoading, timerIsRunning, eventLoading, twilioOnCall, openInactivityModal } = useAppSelector((state) => ({
    timer: state.timer.data,
    timerLoading: state.timer.loading,
    timerIsRunning: state.timer.isRunning,
    eventLoading: state.event.loading,
    twilioOnCall: state.twilio.onCall,
    openInactivityModal: state.modal.inactivityModalParams.open,
  }));
  const { timer: timerDispatch, event: eventDispatch, modal: modalDispatch } = useAppDispatch();
  const [startTimeValue, setStartTimeValue] = useState<number>(DEFAULT_VALUE_0);
  const { days, hours, minutes, seconds, start, pause, reset } = useStopwatch();

  const { initialTimerValue } = getTimerInfo(timer, event?.startDate, event?.endDate);

  const onStartClick = () => {
    if (event) {
      timerDispatch.startTimer({
        eventId: event.id,
        onSuccess: () => start(),
      });
    }
  };

  const onStopClick = () => {
    if (event) {
      timerDispatch.stopTimer({
        eventId: event.id,
        onSuccess: () => pause(),
      });
    }
  };

  const onEditClick = () => modalDispatch.showModal(EModalKey.TimeEditor);

  const onTimeEditorCancelClick = () => modalDispatch.hideModal(EModalKey.TimeEditor);

  const onTimeEditorSaveClick = (milliseconds: number) => {
    if (event) {
      timerDispatch.updateTimer({
        eventId: event.id,
        time: milliseconds,
        onSuccess: () => {
          modalDispatch.hideModal(EModalKey.TimeEditor);
          reset(getStopwatchOffset(milliseconds), false);
        },
      });
    }
  };

  const onInactivityModalResumeClick = () => {
    if (event) {
      timerDispatch.startTimer({
        eventId: event.id,
        onSuccess: () => {
          start();
          modalDispatch.hideModal(EModalKey.Inactivity);
        },
      });
    }
  };

  const onInactivityModalPauseClick = () => {
    modalDispatch.hideModal(EModalKey.Inactivity);
  };

  const onInactivityModalMoveClick = () => {
    modalDispatch.hideModal(EModalKey.Inactivity);
    eventDispatch.setAction(EEventActionType.Back);
    history.back();
  };

  const onIdle = () => {
    if (event) {
      timerDispatch.stopTimer({
        eventId: event.id,
        onSuccess: () => {
          pause();
          modalDispatch.showInactivityModal();
        },
      });
    }
  };

  useEffect(() => {
    if (timer) {
      if (canStart && autoStart) {
        timerDispatch.startTimer({
          eventId: event?.id as number,
          onSuccess: () => reset(initialTimerValue),
        });
      } else {
        reset(initialTimerValue, false);
      }

      if (timer.time?.time) {
        setStartTimeValue(timer.time.time);
      }
    }
  }, [timer, canStart, autoStart]);

  useEffect(() => {
    if (timer) {
      timerDispatch.setUntrackedTime(timerValueToMilliseconds(hours + days * 24, minutes, seconds) - startTimeValue);
    }
  }, [timer, seconds]);

  return (
    <div>
      <div className="mb-5 ff-medium fs-lg">{`Your Timer: ${getTimerFormattedValue(days, hours, minutes, seconds)}`}</div>

      <div className="d-flex align-center" style={{ gap: 16 }}>
        {timerIsRunning ? (
          <Button
            className="orange"
            icon={timerLoading ? <Spin size="small" /> : <PauseCircleOutlined />}
            onClick={onStopClick}
            disabled={timerLoading}
          >
            Stop
          </Button>
        ) : (
          <Button
            className="blue"
            icon={timerLoading ? <Spin size="small" /> : <PlayCircleOutlined />}
            onClick={onStartClick}
            disabled={timerLoading || !canStart}
          >
            Start
          </Button>
        )}

        <Button
          className="default"
          icon={timerLoading ? <Spin size="small" /> : <EditOutlined />}
          disabled={timerIsRunning || timerLoading}
          onClick={onEditClick}
        >
          Edit
        </Button>
      </div>

      <TimeEditorModal
        hours={hours + days * 24}
        minutes={minutes}
        seconds={seconds}
        onSave={onTimeEditorSaveClick}
        onCancel={onTimeEditorCancelClick}
      />

      {timerIsRunning && !twilioOnCall && <IdleTimer timeout={EVENT_IDLE_LIMIT_MS} onIdle={onIdle} />}

      <InactivityModal
        open={openInactivityModal}
        loading={timerLoading || eventLoading}
        onResumeClick={onInactivityModalResumeClick}
        onPauseClick={onInactivityModalPauseClick}
        onMoveClick={onInactivityModalMoveClick}
      />
    </div>
  );
};
